import { computed, ComputedRef } from 'vue';
import type { Content } from '@ilteducation/content-registry-types';
import useSearchContexts from './use-search-contexts';

export type SearchContextOutput = {
  contents: ComputedRef<Content[]>;
  isLoading: ComputedRef<boolean>;
  hasMore: ComputedRef<boolean>;
  hasContents: ComputedRef<boolean>;
  loadMore: () => void;
  hasContentsWithoutBook: ComputedRef<(bookId: string) => boolean>;
  contentsWithoutBook: ComputedRef<(bookId?: string) => Content[]>;
};

const useSearchContext = (contextId: string, limit: number, autoLoad = true): SearchContextOutput => {
  const searchContextStore = useSearchContexts();
  const { search, next, parseSearchId, result } = searchContextStore;

  const isLoading = computed(() => searchContextStore.isLoading(contextId));
  const hasMore = computed(() => searchContextStore.hasMore(contextId));
  const contents = computed(() => result(contextId));
  const contentsWithoutBook = computed(
    () => (bookId?: string) => result(contextId).filter((content) => content.id !== bookId),
  );
  const loadMore = () => next(contextId);
  const hasContents = computed(() => contents.value.length > 0 || hasMore.value);
  const hasContentsWithoutBook = computed(
    () => (bookId: string) => contentsWithoutBook.value(bookId).length > 0 || hasMore.value,
  );

  if (autoLoad && !isLoading.value && hasMore.value) {
    const searchContext = parseSearchId(contextId);

    search(searchContext.contentType, searchContext.searchQuery, limit);
  }
  return {
    contents,
    isLoading,
    hasMore,
    hasContents,
    hasContentsWithoutBook,
    contentsWithoutBook,
    loadMore,
  };
};

export default useSearchContext;
